import React from 'react';
import { CircleCheck, CircleX, RotateCcw } from 'lucide-react';

interface ButtonProps {
    name: string;
    type: 'first' | 'second' | 'third' | 'fourth' | 'recall' | 'update' | 'cancel' | 'success' | 'failed';
    htmlType?: "submit" | "button" | "reset";
    onClick?: (event?: any) => void;
    disabled?: boolean;
    loading?: boolean;
}

const  Button: React.FC<ButtonProps> = ({ name, type, htmlType, onClick, disabled = false, loading = false }) => {
    
    const renderContent = () => {
        if (loading) {
            return (
                <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
            );
        }

        switch (type) {
            case 'success':
                return (
                    <>
                        <CircleCheck className='size-6 text-secondary fill-green-700' />
                        {name}
                    </>
                );

            case 'recall':
                return (
                    <>
                        <RotateCcw className='size-4 text-green-700' />
                        {name}
                    </>
                );

            case 'failed':
                return (
                    <>
                        <CircleX className='size-6 text-secondary fill-red-700' />
                        {name}
                    </>
                );

            default:
                return name;
        }
    };

    const baseClasses = "flex justify-center items-center h-9 rounded-full text-md font-outfit shadow-lg transition-colors duration-150";
    const disabledClasses = "opacity-50 cursor-not-allowed";
    
    const typeClasses: Record<string, string> = {
        first: "bg-primary h-9 hover:bg-golden active:bg-goldenDark text-golden hover:text-primary px-4 md:px-4 lg:px-6 text-md md:text-sm lg:text-md leading-4",
        second: "border-2 h-9 border-textLight bg-secondary text-textColor active:bg-textLight px-2 md:px-3 lg:px-4 p-1",
        third: "bg-goldenHover h-9 w-full hover:bg-golden active:bg-goldenDark text-primary px-2 py-1",
        fourth: "bg-secondary h-9 w-full hover:bg-grayColor text-textColor active:bg-textLight px-2 py-1",
        update: "bg-primary h-9 hover:bg-primary/80 active:bg-primary text-secondary font-semibold px-4 w-full text-md md:text-sm lg:text-md leading-4",
        cancel: "bg-goldenHover h-9 hover:bg-golden active:bg-goldenHover text-secondary font-semibold px-4 w-full",
        success: "bg-secondary h-9 hover:bg-grayColor text-textColor active:bg-textLight px-4 py-1 flex gap-1",
        recall: "bg-secondary h-9 w-full hover:bg-grayColor text-textColor active:bg-textLight px-4 py-1 flex gap-1 w-full",
        failed: "bg-secondary h-9 hover:bg-grayColor text-textColor active:bg-textLight px-4 py-1 flex gap-1",
    };

    return (
        <button
            type={htmlType}
            disabled={disabled || loading}
            onClick={onClick}
            className={`${baseClasses} ${typeClasses[type]} ${disabled || loading ? disabledClasses : ''}`}
        >
            {renderContent()}
        </button>
    );
};

export default Button;
